import { useState, useCallback, useEffect, useRef } from "react";
import {
  IonContent,
  IonPage,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon,
  IonModal,
  IonCheckbox,
  IonLabel,
  IonItem,
} from "@ionic/react";
import {
  addOutline,
  searchOutline,
  layersOutline,
  peopleOutline,
} from "ionicons/icons";
import { useHistory } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import AuthenticationGate from "../components/AuthenticationGate";
import { GroupCard, type GroupCardGroup } from "pymble-ui/components";
import GroupSearch from "../components/groups/GroupSearch";
import CreateGroupModal from "../components/groups/CreateGroupModal";
import BulkGroups from "../components/BulkGroups";
import BulkMembers from "../components/BulkMembers";
import MemberSearch from "../components/MemberSearch";
import { Spinner } from "pymble-ui/components";
import { useMyDetails } from "../hooks/useMyDetails";
import { useGroupBrowse } from "../hooks/useGroupBrowse";

import "./GroupBrowse.css";
import "../components/groups/CreateGroupModal.css";

const StaffGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoading, isError, user } = useMyDetails();

  if (isLoading) return <Spinner message="Checking access..." />;
  if (isError)
    return (
      <Spinner message="Error loading user details. Please refresh." />
    );
  if (!user?.active?.staff) {
    return (
      <div className="ion-padding ion-text-center">
        <h2>Access Denied</h2>
        <p>This application is available to staff only.</p>
      </div>
    );
  }

  return <>{children}</>;
};

const GroupBrowse: React.FC = () => {
  const history = useHistory();
  const { hasAdminAccess } = useMyDetails();
  const {
    groups,
    isLoading,
    filter,
    setFilter,
    toggleSubscription,
    isSubscribing,
    loadMore,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useGroupBrowse();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMemberSearchModal, setShowMemberSearchModal] = useState(false);
  const [showBulkGroupsModal, setShowBulkGroupsModal] = useState(false);
  const [showBulkMembersModal, setShowBulkMembersModal] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(
    new Set()
  );
  const sentinelRef = useRef<HTMLDivElement>(null);

  // A filter is "active" when results are narrowed from the full list
  const hasActiveFilter =
    !!filter.search || filter.type.length > 0 || filter.subscribedOnly;

  // Reset selection when filter changes (server-side filters reset the
  // paged query to page 1 via its query key)
  useEffect(() => {
    setSelectedGroupIds(new Set());
  }, [filter]);

  const hasMore = hasNextPage;

  const toggleGroupSelect = useCallback((groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const allFilteredSelected =
    groups.length > 0 && groups.every((g) => selectedGroupIds.has(g._id));

  const toggleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      setSelectedGroupIds(new Set());
    } else {
      setSelectedGroupIds(new Set(groups.map((g) => g._id)));
    }
  }, [allFilteredSelected, groups]);

  const selectedCount = selectedGroupIds.size;

  // Infinite scroll via IntersectionObserver. Not attached while the
  // client-side subscribedOnly filter is on: with few subscribed matches
  // the sentinel would stay visible and auto-fetch page after page
  // (potentially the whole dataset) - the explicit Load More button
  // remains available instead. Re-arms after each fetch completes
  // (isFetchingNextPage dep) so a still-visible sentinel keeps loading,
  // but not after a failed page fetch (isFetchNextPageError) - that would
  // hammer a failing endpoint; Load More acts as the manual retry.
  useEffect(() => {
    if (
      filter.subscribedOnly ||
      !hasMore ||
      isFetchingNextPage ||
      isFetchNextPageError ||
      !sentinelRef.current
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [
    filter.subscribedOnly,
    hasMore,
    isFetchingNextPage,
    isFetchNextPageError,
    loadMore,
  ]);

  return (
    <IonPage>
      <AppHeader />
      <IonContent fullscreen className="ion-padding">
        <AuthenticationGate>
          <StaffGate>
            <div className="browse-container">
              <div className="browse-header">
                <h1 className="browse-title">Groups</h1>
                {hasAdminAccess && (
                  <div className="browse-admin-actions">
                    <IonButton
                      fill="outline"
                      color="secondary"
                      className="ion-text-capitalize"
                      onClick={() => setShowMemberSearchModal(true)}
                    >
                      <IonIcon slot="start" icon={searchOutline} />
                      Member Search
                    </IonButton>
                    <IonButton
                      fill="outline"
                      color="secondary"
                      className="ion-text-capitalize"
                      onClick={() => setShowBulkGroupsModal(true)}
                      disabled={selectedCount === 0}
                    >
                      <IonIcon slot="start" icon={layersOutline} />
                      Bulk Group{selectedCount > 0 ? ` (${selectedCount})` : ""}
                    </IonButton>
                    <IonButton
                      fill="outline"
                      color="secondary"
                      className="ion-text-capitalize"
                      onClick={() => setShowBulkMembersModal(true)}
                      disabled={selectedCount === 0}
                    >
                      <IonIcon slot="start" icon={peopleOutline} />
                      Bulk Member{selectedCount > 0 ? ` (${selectedCount})` : ""}
                    </IonButton>
                    <IonButton
                      fill="solid"
                      color="secondary"
                      className="ion-text-capitalize"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <IonIcon slot="start" icon={addOutline} />
                      New Group
                    </IonButton>
                  </div>
                )}
              </div>

              <GroupSearch filter={filter} onFilterChange={setFilter} />

              {isLoading ? (
                <Spinner message="Loading groups..." />
              ) : groups.length === 0 && !hasMore ? (
                <div className="browse-empty">
                  <p>No groups found matching your criteria.</p>
                </div>
              ) : (
                <>
                  <div className="browse-count-bar">
                    <span className="browse-count">
                      Showing {groups.length}
                      {hasMore ? "+" : ""} groups
                      {selectedCount > 0 && ` · ${selectedCount} selected`}
                    </span>
                    {hasAdminAccess && hasActiveFilter && (
                      <IonItem lines="none" className="browse-select-all">
                        <IonCheckbox
                          checked={allFilteredSelected}
                          onIonChange={toggleSelectAll}
                        />
                        <IonLabel>
                          Select All ({groups.length}
                          {hasMore ? " loaded" : ""})
                        </IonLabel>
                      </IonItem>
                    )}
                  </div>
                  <IonGrid className="group-grid">
                    <IonRow>
                      {groups.map((group) => (
                        <IonCol
                          key={group._id}
                          size="12"
                          sizeSm="6"
                          sizeMd="4"
                          sizeLg="3"
                          sizeXl="2.4"
                        >
                          <GroupCard
                            group={group as GroupCardGroup}
                            onClick={(id) => history.push(`/groups/${id}`)}
                            onSubscribeClick={toggleSubscription}
                            isSubscribing={isSubscribing}
                            selectable={hasAdminAccess}
                            isSelected={selectedGroupIds.has(group._id)}
                            onSelectClick={toggleGroupSelect}
                          />
                        </IonCol>
                      ))}
                    </IonRow>
                  </IonGrid>
                  {hasMore && (
                    <div className="browse-load-more" ref={sentinelRef}>
                      <IonButton
                        fill="outline"
                        color="medium"
                        className="ion-text-capitalize"
                        onClick={loadMore}
                        disabled={isFetchingNextPage}
                      >
                        {isFetchingNextPage ? "Loading..." : "Load More"}
                      </IonButton>
                    </div>
                  )}
                </>
              )}
            </div>
          </StaffGate>
        </AuthenticationGate>
      </IonContent>

      <IonModal
        isOpen={showCreateModal}
        className="create-group-modal"
        backdropDismiss={true}
        onDidDismiss={() => setShowCreateModal(false)}
      >
        <CreateGroupModal
          onDismiss={() => setShowCreateModal(false)}
          onCreated={(groupId) => {
            setShowCreateModal(false);
            history.push(`/groups/${groupId}`);
          }}
        />
      </IonModal>

      <IonModal
        isOpen={showMemberSearchModal}
        className="member-query-modal"
        backdropDismiss={true}
        onDidDismiss={() => setShowMemberSearchModal(false)}
      >
        <MemberSearch
          onDismiss={() => setShowMemberSearchModal(false)}
          selectedGroups={[]}
        />
      </IonModal>

      <IonModal
        isOpen={showBulkGroupsModal}
        className="bulk-groups-modal"
        backdropDismiss={true}
        onDidDismiss={() => setShowBulkGroupsModal(false)}
      >
        <BulkGroups
          onDismiss={() => setShowBulkGroupsModal(false)}
          onUpdateComplete={() => setShowBulkGroupsModal(false)}
          selectedGroups={Array.from(selectedGroupIds)}
        />
      </IonModal>

      <IonModal
        isOpen={showBulkMembersModal}
        className="bulk-admin-modal"
        backdropDismiss={true}
        onDidDismiss={() => setShowBulkMembersModal(false)}
      >
        <BulkMembers
          onDismiss={() => setShowBulkMembersModal(false)}
          selectedGroups={Array.from(selectedGroupIds)}
        />
      </IonModal>
    </IonPage>
  );
};

export default GroupBrowse;
